// VehicleSection.js
// Vehicle section for the job dialog

import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  TextField
} from '@mui/material';
import FilterSelect from '../common/FilterSelect';
import { getModelsForMake, isVehicleDataReady } from '../../utils/selectUtils';

/**
 * Vehicle section component for the job dialog. Make, model, year and color
 * offer the catalog lists but accept any typed value, since no list is complete.
 * @param {Object} props Component props
 * @param {Object} props.jobData Current job data
 * @param {Function} props.handleInputChange Function to handle input changes
 * @param {Object} props.vehicleData Vehicle data (makes, models, years, colors)
 * @returns {JSX.Element} Vehicle section component
 */
const VehicleSection = ({ jobData, handleInputChange, vehicleData }) => {
  const dataReady = isVehicleDataReady(vehicleData);
  const modelsForMake = dataReady ? getModelsForMake(vehicleData.models, jobData.make) : [];

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>Vehicle</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            margin="dense"
            label="VIN"
            fullWidth
            value={jobData.vin}
            onChange={(e) => {
              const value = e.target.value.toUpperCase();
              if (/^[A-Z0-9]*$/.test(value)) { // Allow only alphanumeric
                handleInputChange('vin', value);
              }
            }}
            placeholder="Valid VIN Autofills Vehicle"
            inputProps={{ maxLength: 17 }} // Add maxLength
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FilterSelect
            label="Make"
            required
            freeText
            error={!jobData.make}
            loading={!dataReady}
            options={dataReady ? vehicleData.makes : []}
            value={jobData.make || ''}
            onChange={(value) => {
              if (value === (jobData.make || '')) return;
              handleInputChange('make', value);
              handleInputChange('model', ''); // Reset model when make changes
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FilterSelect
            label="Model"
            required
            freeText
            error={!jobData.model}
            loading={!dataReady}
            disabled={!jobData.make}
            options={modelsForMake}
            value={jobData.model || ''}
            onChange={(value) => handleInputChange('model', value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FilterSelect
            label="Year"
            required
            freeText
            error={!jobData.year}
            loading={!dataReady}
            options={dataReady ? vehicleData.years : []}
            value={jobData.year || ''}
            onChange={(value) => handleInputChange('year', value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FilterSelect
            label="Color"
            freeText
            loading={!dataReady}
            options={dataReady ? vehicleData.colors : []}
            value={jobData.color || ''}
            onChange={(value) => handleInputChange('color', value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin="dense"
            label="License"
            fullWidth
            value={jobData.license}
            onChange={(e) => {
              const value = e.target.value.toUpperCase();
              if (/^[A-Z0-9]*$/.test(value)) {
                handleInputChange('license', value);
              }
            }}
            inputProps={{ maxLength: 10 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin="dense"
            label="Odometer"
            fullWidth
            value={jobData.odometer}
            onChange={(e) => {
              const value = e.target.value;
              if (/^[0-9]*$/.test(value)) {
                handleInputChange('odometer', value);
              }
            }}
            inputProps={{ maxLength: 7 }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default VehicleSection;
